/**
 * Outil « Archimède : approcher π » — calculs purs.
 *
 * Polygone régulier à n côtés inscrit dans un cercle de rayon 1 : chaque côté
 * mesure 2 sin(π/n), le périmètre Pₙ = 2n sin(π/n) monte vers 2π. Le polygone
 * circonscrit (côté 2 tan(π/n)) descend vers 2π : π est encadré par Pₙ/2 et
 * Qₙ/2, comme le fit Archimède avec 96 côtés.
 */

export const N_MIN = 3;
export const N_MAX = 1000;
/** les doublements d'Archimède (hexagone → 96 côtés) */
export const ARCHIMEDES_STEPS = [3, 6, 12, 24, 48, 96] as const;
const PI_DIGITS = '3.14159265358979';

/** Curseur t ∈ [0 ; 1] → n entier de 3 à 1000 (échelle logarithmique). */
export function nOfSlider(t: number): number {
  const c = Math.min(1, Math.max(0, t));
  return Math.round(N_MIN * (N_MAX / N_MIN) ** c);
}

export function sliderOfN(n: number): number {
  return Math.log(n / N_MIN) / Math.log(N_MAX / N_MIN);
}

export const sideInscribed = (n: number) => 2 * Math.sin(Math.PI / n);
export const perimeterInscribed = (n: number) => n * sideInscribed(n);
export const perimeterCircumscribed = (n: number) => 2 * n * Math.tan(Math.PI / n);

/** Sommets du polygone inscrit (rayon 1), le premier en haut. */
export function polygonVertices(n: number): [number, number][] {
  return Array.from({ length: n }, (_, k) => {
    const a = Math.PI / 2 + (2 * Math.PI * k) / n;
    return [Math.cos(a), Math.sin(a)];
  });
}

/** Sommets du polygone circonscrit (côtés tangents au cercle aux sommets inscrits). */
export function circumscribedVertices(n: number): [number, number][] {
  const r = 1 / Math.cos(Math.PI / n);
  return Array.from({ length: n }, (_, k) => {
    const a = Math.PI / 2 + (2 * Math.PI * (k + 0.5)) / n;
    return [r * Math.cos(a), r * Math.sin(a)];
  });
}

/** 3,14159… tronqué à `d` décimales, à la française. */
export function truncText(v: number, d: number): string {
  const f = 10 ** d;
  // marge : 5,9999999999 (calcul en flottants) s'écrit 6,000000
  return (Math.trunc(v * f + 1e-7) / f).toFixed(d).replace('.', ',');
}

/** Nombre de décimales de π justes dans `v` (comparaison des écritures tronquées). */
export function correctDecimals(v: number): number {
  const s = (Math.trunc(v * 1e12 + 1e-3) / 1e12).toFixed(12);
  let k = 0;
  while (k < 12 && s[2 + k] === PI_DIGITS[2 + k] && s.slice(0, 2) === '3.') k += 1;
  return s.slice(0, 2) === '3.' ? k : 0;
}
